import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import {
  decide,
  OUTCOME_INSTRUMENTS,
  type PlanOfCareResponse,
  type VisitResponse,
} from '@emr/shared';

import {
  createHomeExerciseProgram,
  createPlanOfCare,
  createVisit,
  fetchHomeExercisePrograms,
  fetchOutcomeMeasures,
  fetchPlansOfCare,
  fetchVisits,
  recordOutcomeMeasure,
} from '../api/physicalTherapy.ts';
import { useGrants } from '../auth/sessionContext.ts';
import { Button, ErrorText, Field, Panel } from '../components/primitives.tsx';
import { GatedRoute } from '../components/GatedRoute.tsx';
import { QuerySection } from '../components/QuerySection.tsx';

/**
 * The physical therapy & rehab module's real, patient-scoped surface (`BACKLOG.md` M6). Reachable
 * directly by URL even with the catalog entry showing an upgrade prompt for an unlicensed org,
 * matching `PatientDermatologyPage.tsx`/`PatientAestheticsPage.tsx`'s own stated reasoning for
 * re-applying `GatedRoute` here.
 *
 * Four sections, each independently queried and independently writable behind `chart:write`:
 *
 *   - **Plans of care** — the POC lifecycle (eval, establishment, physician signature,
 *     certification period, discharge). The due/overdue tracks (`status` on the response) are
 *     computed server-side from `planOfCareStatus`, never re-derived here.
 *   - **Visits** — one treatment visit's timed CPT-code minutes, with billed units computed
 *     server-side via the 8-minute-rule engine (**UNREVIEWED thresholds — see
 *     `shared/src/physicalTherapy/eightMinuteRule.ts`'s own header**, surfaced verbatim in this
 *     page's own copy rather than hidden behind a plain number).
 *   - **Home exercise programs** — a named prescription plus its exercise list.
 *   - **Scored outcome instruments** — a raw score entered against one of the four supported
 *     instruments (ODI/LEFS/DASH/NDI); this page never renders any instrument's own item content,
 *     matching `outcomeInstruments.ts`'s own scope boundary.
 *
 * **Deliberately not built in this pass**: a UI for the exercise flowsheet
 * (`POST .../visits/:visitId/exercise-flowsheet-entries`) — the route and its full audit/access
 * coverage exist and are tested, but no form consumes it yet, the same "server complete, UI
 * deferred" shape this codebase already accepts elsewhere (e.g. `C22`'s own noted gaps). A future
 * pass adding a per-visit detail view is the natural place to add it.
 */
export function PatientPhysicalTherapyPage() {
  const { t } = useTranslation('physicalTherapy');
  const { patientId } = useParams<{ patientId: string }>();

  if (patientId === undefined) {
    return <p className="m-0 text-sm text-danger">{t('common.noPatientId')}</p>;
  }

  return (
    <GatedRoute title={t('pageTitle')} capability="chart:read" feature="physicalTherapyModule">
      <div className="flex flex-col gap-6">
        <PlansOfCareSection patientId={patientId} />
        <VisitsSection patientId={patientId} />
        <HomeExerciseProgramsSection patientId={patientId} />
        <OutcomeMeasuresSection patientId={patientId} />
      </div>
    </GatedRoute>
  );
}

function SectionHeading({ children }: { readonly children: string }) {
  return <h2 className="m-0 text-lg font-semibold text-ink">{children}</h2>;
}

const FREE_TEXT = 'break-words whitespace-normal [overflow-wrap:anywhere]';

// --- Plans of care --------------------------------------------------------------------------------

function PlansOfCareSection({ patientId }: { readonly patientId: string }) {
  const { t } = useTranslation('physicalTherapy');
  const grants = useGrants();
  const mayWrite = decide(grants, 'chart:write');
  const queryClient = useQueryClient();
  const queryKey = ['physicalTherapy', 'plansOfCare', patientId];

  const query = useQuery<readonly PlanOfCareResponse[]>({
    queryKey,
    queryFn: ({ signal }) => fetchPlansOfCare(patientId, signal),
  });

  const [evalDate, setEvalDate] = useState('');

  const create = useMutation({
    mutationFn: () => createPlanOfCare(patientId, { evalDate, confidentialityReasons: [] }),
    onSuccess: () => {
      setEvalDate('');
      void queryClient.invalidateQueries({ queryKey });
    },
  });

  return (
    <section className="flex flex-col gap-2">
      <SectionHeading>{t('plansOfCare.heading')}</SectionHeading>
      <p className="m-0 text-sm text-ink-muted">{t('plansOfCare.unreviewedNote')}</p>
      <Panel>
        <QuerySection
          query={query}
          loadingLabel={t('common.loading')}
          isEmpty={(data) => data.length === 0}
          emptyMessage={t('plansOfCare.emptyMessage')}
        >
          {(plans) => (
            <ul className="m-0 flex list-none flex-col gap-2 p-0">
              {plans.map((poc) => (
                <li key={poc.id} className="rounded-control border border-line p-3 text-sm">
                  <p className="m-0 font-medium text-ink">
                    {t('plansOfCare.evalDate', { date: poc.evalDate })}
                  </p>
                  <p className="m-0 text-ink-muted">
                    {t('plansOfCare.overallState', {
                      state: t(`plansOfCare.states.${poc.status.overallState}`),
                    })}
                  </p>
                  <p className="m-0 text-ink-muted">
                    {t('plansOfCare.signatureState', {
                      state: t(`plansOfCare.dueStates.${poc.status.signatureState}`),
                    })}
                    {' · '}
                    {t('plansOfCare.certificationState', {
                      state: t(`plansOfCare.dueStates.${poc.status.certificationState}`),
                    })}
                    {' · '}
                    {t('plansOfCare.progressReportState', {
                      state: t(`plansOfCare.dueStates.${poc.status.progressReportState}`),
                    })}
                  </p>
                  {poc.dischargeSummary !== null ? (
                    <p className={`m-0 text-ink-muted ${FREE_TEXT}`}>{poc.dischargeSummary}</p>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </QuerySection>

        {mayWrite ? (
          <form
            onSubmit={(event) => {
              event.preventDefault();
              create.mutate();
            }}
            className="mt-4 flex flex-col gap-3 border-t border-row-line pt-4 sm:flex-row sm:flex-wrap sm:items-end"
          >
            <Field
              label={t('plansOfCare.evalDateFieldLabel')}
              type="date"
              value={evalDate}
              onChange={(event) => {
                setEvalDate(event.target.value);
              }}
              required
            />
            <Button type="submit" pending={create.isPending}>
              {t('plansOfCare.createButton')}
            </Button>
          </form>
        ) : null}
        {create.isError ? <ErrorText message={t('plansOfCare.createError')} /> : null}
      </Panel>
    </section>
  );
}

// --- Visits -----------------------------------------------------------------------------------------

function VisitsSection({ patientId }: { readonly patientId: string }) {
  const { t } = useTranslation('physicalTherapy');
  const grants = useGrants();
  const mayWrite = decide(grants, 'chart:write');
  const queryClient = useQueryClient();
  const queryKey = ['physicalTherapy', 'visits', patientId];
  const plansQueryKey = ['physicalTherapy', 'plansOfCare', patientId];

  const query = useQuery<readonly VisitResponse[]>({
    queryKey,
    queryFn: ({ signal }) => fetchVisits(patientId, signal),
  });
  const plans = useQuery<readonly PlanOfCareResponse[]>({
    queryKey: plansQueryKey,
    queryFn: ({ signal }) => fetchPlansOfCare(patientId, signal),
  });

  const [planOfCareId, setPlanOfCareId] = useState('');
  const [visitDate, setVisitDate] = useState('');
  const [cptCode, setCptCode] = useState('');
  const [minutes, setMinutes] = useState('');

  const create = useMutation({
    mutationFn: () =>
      createVisit(patientId, {
        planOfCareId,
        visitDate,
        timedUnits: [{ cptCode, minutes: Number(minutes) }],
        confidentialityReasons: [],
      }),
    onSuccess: () => {
      setVisitDate('');
      setCptCode('');
      setMinutes('');
      void queryClient.invalidateQueries({ queryKey });
    },
  });

  return (
    <section className="flex flex-col gap-2">
      <SectionHeading>{t('visits.heading')}</SectionHeading>
      <p className="m-0 text-sm text-ink-muted">{t('visits.unreviewedNote')}</p>
      <Panel>
        <QuerySection
          query={query}
          loadingLabel={t('common.loading')}
          isEmpty={(data) => data.length === 0}
          emptyMessage={t('visits.emptyMessage')}
        >
          {(visits) => (
            <ul className="m-0 flex list-none flex-col gap-2 p-0">
              {visits.map((visit) => (
                <li key={visit.id} className="rounded-control border border-line p-3 text-sm">
                  <p className="m-0 font-medium text-ink">
                    {t('visits.visitDate', { date: visit.visitDate })}
                  </p>
                  {visit.billingRefused ? (
                    <p className="m-0 text-danger">{t('visits.billingRefused')}</p>
                  ) : (
                    <p className="m-0 text-ink-muted">
                      {t('visits.billedSummary', {
                        minutes: visit.totalMinutes,
                        units: visit.totalUnits,
                      })}
                    </p>
                  )}
                  <ul className="m-0 flex list-none flex-col gap-0.5 p-0 text-ink-muted">
                    {visit.billedLines.map((line) => (
                      <li key={line.cptCode} className={FREE_TEXT}>
                        {t('visits.billedLine', { code: line.cptCode, units: line.units })}
                      </li>
                    ))}
                  </ul>
                  {visit.notes !== null ? (
                    <p className={`m-0 text-ink-muted ${FREE_TEXT}`}>{visit.notes}</p>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </QuerySection>

        {mayWrite ? (
          <form
            onSubmit={(event) => {
              event.preventDefault();
              create.mutate();
            }}
            className="mt-4 flex flex-col gap-3 border-t border-row-line pt-4 sm:flex-row sm:flex-wrap sm:items-end"
          >
            <label className="flex flex-col gap-1.5">
              <span className="text-sm font-medium text-ink">{t('visits.planOfCareLabel')}</span>
              <select
                value={planOfCareId}
                onChange={(event) => {
                  setPlanOfCareId(event.target.value);
                }}
                required
                className="rounded-control border border-field-line bg-field px-3 py-2 text-base text-ink sm:text-sm"
              >
                <option value="">{t('visits.planOfCarePlaceholder')}</option>
                {(plans.data ?? []).map((poc) => (
                  <option key={poc.id} value={poc.id}>
                    {poc.evalDate}
                  </option>
                ))}
              </select>
            </label>
            <Field
              label={t('visits.visitDateFieldLabel')}
              type="date"
              value={visitDate}
              onChange={(event) => {
                setVisitDate(event.target.value);
              }}
              required
            />
            <Field
              label={t('visits.cptCodeFieldLabel')}
              value={cptCode}
              onChange={(event) => {
                setCptCode(event.target.value);
              }}
              required
            />
            <Field
              label={t('visits.minutesFieldLabel')}
              type="number"
              min="1"
              value={minutes}
              onChange={(event) => {
                setMinutes(event.target.value);
              }}
              required
            />
            <Button type="submit" pending={create.isPending}>
              {t('visits.createButton')}
            </Button>
          </form>
        ) : null}
        {create.isError ? <ErrorText message={t('visits.createError')} /> : null}
      </Panel>
    </section>
  );
}

// --- Home exercise programs -----------------------------------------------------------------------

function HomeExerciseProgramsSection({ patientId }: { readonly patientId: string }) {
  const { t } = useTranslation('physicalTherapy');
  const grants = useGrants();
  const mayWrite = decide(grants, 'chart:write');
  const queryClient = useQueryClient();
  const queryKey = ['physicalTherapy', 'homeExercisePrograms', patientId];

  const query = useQuery({
    queryKey,
    queryFn: ({ signal }) => fetchHomeExercisePrograms(patientId, signal),
  });

  const [name, setName] = useState('');
  const [prescribedOn, setPrescribedOn] = useState('');
  const [exerciseName, setExerciseName] = useState('');

  const create = useMutation({
    mutationFn: () =>
      createHomeExerciseProgram(patientId, {
        name,
        prescribedOn,
        items: [{ exerciseName }],
        confidentialityReasons: [],
      }),
    onSuccess: () => {
      setName('');
      setPrescribedOn('');
      setExerciseName('');
      void queryClient.invalidateQueries({ queryKey });
    },
  });

  return (
    <section className="flex flex-col gap-2">
      <SectionHeading>{t('homeExercisePrograms.heading')}</SectionHeading>
      <Panel>
        <QuerySection
          query={query}
          loadingLabel={t('common.loading')}
          isEmpty={(data) => data.length === 0}
          emptyMessage={t('homeExercisePrograms.emptyMessage')}
        >
          {(programs) => (
            <ul className="m-0 flex list-none flex-col gap-2 p-0">
              {programs.map((program) => (
                <li key={program.id} className="rounded-control border border-line p-3 text-sm">
                  <p className={`m-0 font-medium text-ink ${FREE_TEXT}`}>{program.name}</p>
                  <p className="m-0 text-ink-muted">
                    {t('homeExercisePrograms.prescribedOn', { date: program.prescribedOn })}
                  </p>
                  <ul className="m-0 flex list-none flex-col gap-0.5 p-0 text-ink-muted">
                    {program.items.map((item) => (
                      <li key={item.id} className={FREE_TEXT}>
                        {item.exerciseName}
                      </li>
                    ))}
                  </ul>
                </li>
              ))}
            </ul>
          )}
        </QuerySection>

        {mayWrite ? (
          <form
            onSubmit={(event) => {
              event.preventDefault();
              create.mutate();
            }}
            className="mt-4 flex flex-col gap-3 border-t border-row-line pt-4 sm:flex-row sm:flex-wrap sm:items-end"
          >
            <Field
              label={t('homeExercisePrograms.nameFieldLabel')}
              value={name}
              onChange={(event) => {
                setName(event.target.value);
              }}
              required
            />
            <Field
              label={t('homeExercisePrograms.prescribedOnFieldLabel')}
              type="date"
              value={prescribedOn}
              onChange={(event) => {
                setPrescribedOn(event.target.value);
              }}
              required
            />
            <Field
              label={t('homeExercisePrograms.exerciseNameFieldLabel')}
              value={exerciseName}
              onChange={(event) => {
                setExerciseName(event.target.value);
              }}
              required
            />
            <Button type="submit" pending={create.isPending}>
              {t('homeExercisePrograms.createButton')}
            </Button>
          </form>
        ) : null}
        {create.isError ? <ErrorText message={t('homeExercisePrograms.createError')} /> : null}
      </Panel>
    </section>
  );
}

// --- Scored outcome instruments ---------------------------------------------------------------------

function OutcomeMeasuresSection({ patientId }: { readonly patientId: string }) {
  const { t } = useTranslation('physicalTherapy');
  const grants = useGrants();
  const mayWrite = decide(grants, 'chart:write');
  const queryClient = useQueryClient();
  const queryKey = ['physicalTherapy', 'outcomeMeasures', patientId];

  const query = useQuery({
    queryKey,
    queryFn: ({ signal }) => fetchOutcomeMeasures(patientId, signal),
  });

  const [instrument, setInstrument] = useState<(typeof OUTCOME_INSTRUMENTS)[number]>('LEFS');
  const [rawScore, setRawScore] = useState('');
  const [scoredOn, setScoredOn] = useState('');

  const create = useMutation({
    mutationFn: () =>
      recordOutcomeMeasure(patientId, {
        instrument,
        rawScore,
        scoredOn,
        confidentialityReasons: [],
      }),
    onSuccess: () => {
      setRawScore('');
      setScoredOn('');
      void queryClient.invalidateQueries({ queryKey });
    },
  });

  return (
    <section className="flex flex-col gap-2">
      <SectionHeading>{t('outcomeMeasures.heading')}</SectionHeading>
      <p className="m-0 text-sm text-ink-muted">{t('outcomeMeasures.licensingNote')}</p>
      <Panel>
        <QuerySection
          query={query}
          loadingLabel={t('common.loading')}
          isEmpty={(data) => data.length === 0}
          emptyMessage={t('outcomeMeasures.emptyMessage')}
        >
          {(measures) => (
            <ul className="m-0 flex list-none flex-col gap-2 p-0">
              {measures.map((measure) => (
                <li key={measure.id} className="rounded-control border border-line p-3 text-sm">
                  <p className="m-0 font-medium text-ink">
                    {t('outcomeMeasures.entry', {
                      instrument: measure.instrument,
                      score: measure.rawScore,
                      date: measure.scoredOn,
                    })}
                  </p>
                  {measure.percentDisability !== null ? (
                    <p className="m-0 text-ink-muted">
                      {t('outcomeMeasures.percentDisability', {
                        percent: measure.percentDisability,
                      })}
                    </p>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </QuerySection>

        {mayWrite ? (
          <form
            onSubmit={(event) => {
              event.preventDefault();
              create.mutate();
            }}
            className="mt-4 flex flex-col gap-3 border-t border-row-line pt-4 sm:flex-row sm:flex-wrap sm:items-end"
          >
            <label className="flex flex-col gap-1.5">
              <span className="text-sm font-medium text-ink">
                {t('outcomeMeasures.instrumentLabel')}
              </span>
              <select
                value={instrument}
                onChange={(event) => {
                  setInstrument(event.target.value as (typeof OUTCOME_INSTRUMENTS)[number]);
                }}
                className="rounded-control border border-field-line bg-field px-3 py-2 text-base text-ink sm:text-sm"
              >
                {OUTCOME_INSTRUMENTS.map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
            </label>
            <Field
              label={t('outcomeMeasures.rawScoreFieldLabel')}
              value={rawScore}
              onChange={(event) => {
                setRawScore(event.target.value);
              }}
              required
            />
            <Field
              label={t('outcomeMeasures.scoredOnFieldLabel')}
              type="date"
              value={scoredOn}
              onChange={(event) => {
                setScoredOn(event.target.value);
              }}
              required
            />
            <Button type="submit" pending={create.isPending}>
              {t('outcomeMeasures.recordButton')}
            </Button>
          </form>
        ) : null}
        {create.isError ? <ErrorText message={t('outcomeMeasures.createError')} /> : null}
      </Panel>
    </section>
  );
}
